import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price while preserving paycheck price functionality
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      // Store the current paycheck price element if it exists
      const currentPaycheckElement = currentPrice.querySelector('.price-per-paycheck');
      
      // Replace the price container
      currentPrice.replaceWith(newPrice);
      
      // If we had a paycheck price element, restore it or update it
      if (currentPaycheckElement) {
        const newPriceContainer = this.querySelector('[ref="priceContainer"]');
        let newPaycheckElement = newPriceContainer.querySelector('.price-per-paycheck');
        
        if (newPaycheckElement) {
          // Update the data-price-raw attribute with the new variant price
          const newVariantPrice = event.detail.resource?.price;
          if (newVariantPrice) {
            newPaycheckElement.dataset.priceRaw = newVariantPrice;
            newPaycheckElement.dataset.variantId = event.detail.resource?.id || '';
            
            // Trigger a custom event to update the paycheck price display
            this.updatePaycheckPrice(newPaycheckElement, newVariantPrice);
          }
        } else {
          // If the new price doesn't have a paycheck element, add it back
          const priceSpan = newPriceContainer.querySelector('.price');
          if (priceSpan) {
            // Clone the old paycheck element and update its data
            newPaycheckElement = currentPaycheckElement.cloneNode(true);
            const newVariantPrice = event.detail.resource?.price;
            if (newVariantPrice) {
              newPaycheckElement.dataset.priceRaw = newVariantPrice;
              newPaycheckElement.dataset.variantId = event.detail.resource?.id || '';
            }
            
            // Insert after the price span
            priceSpan.insertAdjacentElement('afterend', newPaycheckElement);
            
            // Update the display
            this.updatePaycheckPrice(newPaycheckElement, newVariantPrice);
          }
        }
      }
    }
  };
  
  /**
   * Updates the paycheck price display based on current deduction settings
   * @param {HTMLElement} paycheckElement - The paycheck price element
   * @param {number} price - The variant price in cents
   */
  updatePaycheckPrice(paycheckElement, price) {
    // Check if PaycheckDeductions is available
    if (typeof window.PaycheckDeductions === 'undefined') {
      // If not available yet, try again in a moment
      setTimeout(() => this.updatePaycheckPrice(paycheckElement, price), 100);
      return;
    }
    
    // Get customer tags if available
    let customerTags = [];
    const tagsData = document.getElementById('global-customer-tags');
    if (tagsData) {
      try {
        customerTags = JSON.parse(tagsData.textContent);
      } catch (e) {
        console.error('Error parsing customer tags:', e);
      }
    }
    
    // Get the deduction count
    const deductionInfo = window.PaycheckDeductions.getDeductionCount(customerTags);
    const deductionCount = deductionInfo.count;
    
    // Calculate and update the paycheck price
    const perPaycheckAmount = (price / deductionCount) / 100;
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(perPaycheckAmount);
    
    const priceSpan = paycheckElement.querySelector('.paycheck-price');
    if (priceSpan) {
      priceSpan.textContent = formatted + '/paycheck';
    }
    
    // Dispatch a custom event to notify that paycheck price was updated
    this.dispatchEvent(new CustomEvent('paycheck-price-updated', {
      detail: {
        price: price,
        deductionCount: deductionCount,
        formatted: formatted
      },
      bubbles: true
    }));
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}